import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './ApproveCustomers.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ApproveCustomers = () => {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [tab, setTab] = useState('pending'); // 'pending' | 'assign-offer'

  // ── Pending approval state ───────────────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  // ── Assign offer state ───────────────────────────────────────────────
  const [allCustomers, setAllCustomers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [search, setSearch] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/online-admin/pending-customers`, auth());
      setCustomers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load pending customers.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferData = async () => {
    try {
      setOfferLoading(true);
      const [custRes, offRes] = await Promise.all([
        axios.get(`${API_URL}/offers/admin/customers`, auth()),
        axios.get(`${API_URL}/offers/admin`, auth()),
      ]);
      setAllCustomers(custRes.data);
      setOffers(offRes.data.filter(o => o.active));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data.');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleTabChange = (t) => {
    setTab(t);
    setError('');
    setSuccess('');
    if (t === 'assign-offer' && allCustomers.length === 0) fetchOfferData();
  };

  const handleApprove = async (id, name) => {
    setApproving(id);
    setError('');
    try {
      await axios.put(`${API_URL}/online-admin/approve/${id}`, {}, auth());
      setSuccess(`${name} approved successfully.`);
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Approval failed.');
    } finally {
      setApproving(null);
    }
  };

  const handleAssignOffer = async (customerId, offerId) => {
    setAssigningId(customerId);
    setError('');
    try {
      await axios.put(
        `${API_URL}/offers/admin/customer/${customerId}/offer`,
        { offerId: offerId || null },
        auth()
      );
      setAllCustomers(prev => prev.map(c => {
        if (c.id !== customerId) return c;
        const offer = offers.find(o => o.id === parseInt(offerId, 10));
        return {
          ...c,
          offer_id:    offer ? offer.id    : null,
          offer_title: offer ? offer.title : null,
          offer_code:  offer ? offer.code  : null,
        };
      }));
      setSuccess(offerId ? 'Offer assigned.' : 'Offer cleared.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update offer.');
    } finally {
      setAssigningId(null);
    }
  };

  const filteredCustomers = allCustomers.filter(c =>
    !search ||
    (c.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ac-page">

      {/* Header */}
      <div className="ac-header">
        <div>
          <h1 className="ac-title">Customers</h1>
          <p className="ac-subtitle">Approve registrations and manage offer assignments</p>
        </div>
        {tab === 'pending' && (
          <div className="ac-badge-wrap">
            <span className="ac-pending-badge">{customers.length} pending</span>
            <button className="ac-refresh-btn" onClick={fetchPending} disabled={loading}>↺ Refresh</button>
          </div>
        )}
        {tab === 'assign-offer' && (
          <div className="ac-badge-wrap">
            <button className="ac-refresh-btn" onClick={fetchOfferData} disabled={offerLoading}>↺ Refresh</button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="ac-tabs">
        <button
          className={`ac-tab${tab === 'pending' ? ' ac-tab--active' : ''}`}
          onClick={() => handleTabChange('pending')}
        >
          Pending Approval
          {customers.length > 0 && <span className="ac-tab-badge">{customers.length}</span>}
        </button>
        <button
          className={`ac-tab${tab === 'assign-offer' ? ' ac-tab--active' : ''}`}
          onClick={() => handleTabChange('assign-offer')}
        >
          Assign Offer
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* ── Pending Approval tab ── */}
      {tab === 'pending' && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="no-data">Loading…</td></tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-data">
                    <div className="ac-empty">
                      <span className="ac-empty-icon">✅</span>
                      <p>No pending approvals</p>
                    </div>
                  </td>
                </tr>
              ) : customers.map(c => (
                <tr key={c.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">{(c.name || c.customer_name || '?')[0].toUpperCase()}</div>
                      <div>
                        <div className="ac-name">{c.name || c.customer_name}</div>
                        <div className="ac-username">{c.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>{c.email || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>{c.phone || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td className="ac-address">{c.address || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>
                    <button
                      className="ac-approve-btn"
                      onClick={() => handleApprove(c.id, c.name || c.customer_name)}
                      disabled={approving === c.id}
                    >
                      {approving === c.id ? 'Approving…' : '✓ Approve'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Assign Offer tab ── */}
      {tab === 'assign-offer' && (
        <>
          <div className="ac-search-bar">
            <input
              type="text"
              placeholder="Search by name, phone or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="ac-search-input"
            />
          </div>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Current Offer</th>
                  <th>Assign Offer</th>
                </tr>
              </thead>
              <tbody>
                {offerLoading ? (
                  <tr><td colSpan={4} className="no-data">Loading…</td></tr>
                ) : filteredCustomers.length === 0 ? (
                  <tr><td colSpan={4} className="no-data">No customers found.</td></tr>
                ) : filteredCustomers.map(c => (
                  <tr key={c.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">{(c.customer_name || '?')[0].toUpperCase()}</div>
                        <div>
                          <div className="ac-name">{c.customer_name}</div>
                          <div className="ac-username">{c.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>{c.phone || <span style={{ color: '#bbb' }}>—</span>}</td>
                    <td>
                      {c.offer_id
                        ? <span className="ac-offer-badge">{c.offer_title}{c.offer_code ? ` (${c.offer_code})` : ''}</span>
                        : <span style={{ color: '#bbb' }}>None</span>}
                    </td>
                    <td>
                      <div className="ac-offer-actions">
                        <select
                          className="ac-offer-select"
                          value={c.offer_id || ''}
                          onChange={e => handleAssignOffer(c.id, e.target.value)}
                          disabled={assigningId === c.id}
                        >
                          <option value="">— None —</option>
                          {offers.map(o => (
                            <option key={o.id} value={o.id}>
                              {o.title}{o.code ? ` (${o.code})` : ''}
                            </option>
                          ))}
                        </select>
                        {assigningId === c.id && <span className="ac-saving">Saving…</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ApproveCustomers;
